import jsQR from 'jsqr';

// Original (upload) decoder uses 1600 to stay light when the operator drops in
// a folder of 20+ images at once. The viewer-side location decoder bumps to
// 2400 — operators only decode one displayed page at a time, and a small
// printed QR sticker on a 2480 px scan can land below jsQR's detection
// threshold after a 0.65× downscale. The location bbox is returned in 0..1
// fractions regardless of the scale used.
const UPLOAD_MAX_SIDE = 1600;
const LOCATION_MAX_SIDE = 2400;

/** Persisted QR location: 4 corner points in 0..1 fractions of the un-rotated
 *  natural image. Stored as JSON on `batch_page_records.qr_bbox` and returned
 *  by `BatchPageRecordResource`. Both photo-scanning and qr-review render the
 *  overlay from this shape, so the type lives in the shared lib. */
export interface QrBboxData {
  corners: { x: number; y: number }[];
}

export interface QrLocationInfo {
  data: string;
  /** Axis-aligned bounding box in 0..1 fractions of the natural image size. */
  bbox: { left: number; top: number; width: number; height: number };
  /** Four corner points in 0..1 fractions, in top-left → top-right → bottom-right → bottom-left order. */
  corners: { x: number; y: number }[];
}

interface Raster {
  imageData: ImageData;
  width: number;
  height: number;
}

async function rasteriseForDecode(
  blob: Blob,
  maxSide: number
): Promise<Raster | null> {
  if (typeof window === 'undefined') return null;
  if (typeof createImageBitmap !== 'function') return null;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    return null;
  }

  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > maxSide ? maxSide / longest : 1;
    const targetWidth = Math.max(1, Math.round(bitmap.width * scale));
    const targetHeight = Math.max(1, Math.round(bitmap.height * scale));

    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(targetWidth, targetHeight)
        : Object.assign(document.createElement('canvas'), {
            width: targetWidth,
            height: targetHeight
          });

    const ctx = (canvas as OffscreenCanvas | HTMLCanvasElement).getContext(
      '2d'
    ) as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
    if (!ctx) return null;

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    return {
      imageData: ctx.getImageData(0, 0, targetWidth, targetHeight),
      width: targetWidth,
      height: targetHeight
    };
  } catch {
    return null;
  } finally {
    bitmap.close?.();
  }
}

/**
 * Decode a QR code from a scanned-page Blob. Returns the decoded string, or
 * null if no QR is found / the image cannot be read. Used by the scanner
 * upload pipeline to bind each uploaded image to the page slot whose printed
 * QR matches — without it we fall back to server-side auto-numbering.
 *
 * Downscales the source bitmap to a 1600px longest edge before decoding so a
 * 4000×6000 high-DPI scan doesn't allocate a 96 MB ImageData buffer.
 */
export async function decodeQrFromBlob(blob: Blob): Promise<string | null> {
  const raster = await rasteriseForDecode(blob, UPLOAD_MAX_SIDE);
  if (!raster) return null;
  const result = jsQR(raster.imageData.data, raster.width, raster.height, {
    inversionAttempts: 'attemptBoth'
  });
  return result?.data?.trim() || null;
}

/**
 * Decode a QR and also return its location in 0..1 fractions relative to the
 * natural image. Used by the document viewer to overlay a status-coloured
 * polygon directly over the printed QR — operators can see at a glance which
 * page is mismatched / unverified without leaving the preview.
 *
 * Shares the rasterisation pipeline with `decodeQrFromBlob`. Fractions are
 * resolution-independent so the caller can position the overlay in the
 * un-rotated image's coordinate space.
 */
export async function decodeQrLocationFromBlob(
  blob: Blob
): Promise<QrLocationInfo | null> {
  const raster = await rasteriseForDecode(blob, LOCATION_MAX_SIDE);
  if (!raster) return null;
  return decodeQrLocationFromImageData(
    raster.imageData,
    raster.width,
    raster.height
  );
}

/**
 * Decode a QR from already-rasterised ImageData. Use this when the caller has
 * already loaded the image into a canvas (e.g. via an `<img>` element) — it
 * skips fetching the blob, which matters in environments where a browser
 * extension intercepts `fetch()` of blob URLs and breaks it.
 *
 * The caller is responsible for any downscaling — pass full-resolution
 * ImageData to maximise the chance of detecting small printed QRs.
 */
export function decodeQrLocationFromImageData(
  imageData: ImageData,
  width: number,
  height: number
): QrLocationInfo | null {
  const result = jsQR(imageData.data, width, height, {
    inversionAttempts: 'attemptBoth'
  });
  if (!result?.data) return null;

  const loc = result.location;
  const cornerPx = [
    loc.topLeftCorner,
    loc.topRightCorner,
    loc.bottomRightCorner,
    loc.bottomLeftCorner
  ];
  const corners = cornerPx.map((c) => ({
    x: Math.max(0, Math.min(1, c.x / width)),
    y: Math.max(0, Math.min(1, c.y / height))
  }));
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);

  return {
    data: result.data.trim(),
    bbox: {
      left,
      top,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top)
    },
    corners
  };
}
